import { AriaAttributes, forwardRef, Fragment, ReactNode } from 'react';
import styled from 'styled-components';

import { ButtonColor, ButtonSize, IconColor, IconSize, Icons } from '../enums';
import Icon from './Icon';
import TooltipInfo from './TooltipInfo';
import useIsMobile from '@/helpers/useIsMobile';
const buttonClasses = `flex font-medium leading-none relative border transition duration-200 ease-in-out focus:outline-primary active:outline-offset-2 active:outline active:outline-slate-800/20 [&>*]:relative [&>*]:z-10 after:content-[''] after:transition hover:after:opacity-50 after:absolute after:z-0 after:inset-px after:shadow-[0_0_0_1px_rgba(255,255,255,0.25)] after:bg-[radial-gradient(100%_100%_at_50%_100%,_rgba(255,255,255,0.25)_0%,_rgba(255,255,255,0)_100%)] has-[class*='remove']:opacity-10`;
const colorClasses: Record<ButtonColor, string> = {
  [ButtonColor.primary]:
    'bg-primary border-primary backdrop-blur text-white before:border-t-slate-50 before:border-slate-50/5 [&>*]:drop-shadow-[0_-1px_0_rgb(var(--color-red))]',
    [ButtonColor.warning]:
    'bg-red-700 border-red-900 backdrop-blur text-white before:border-t-slate-50 before:border-slate-50/5 [&>*]:drop-shadow-[0_-1px_0_rgb(var(--color-primary))]',
  [ButtonColor.secondary]:
    'bg-slate-800 border-slate-800 backdrop-blur text-white before:border-t-slate-50 before:border-slate-50/5',
  [ButtonColor.transparent]:
    'bg-white/0 border-transparent after:hidden hover:border-slate-200 before:border-t-slate-800 before:border-slate-800/5',
  [ButtonColor.light]:
    'bg-white/90 border-white backdrop-blur text-slate-600 before:border-t-slate-800 before:border-slate-800/5',
  [ButtonColor.grey]:
    'bg-white/0 border-slate-800/5 text-slate-500 hover:bg-slate-800/5 hover:text-slate-800 after:hidden before:border-t-slate-800 before:border-slate-800/5',
  [ButtonColor.border]:
    'bg-white/0 border-slate-300 text-slate-600 hover:border-slate-200 before:border-t-slate-800 before:border-slate-800/5',
  [ButtonColor.default]:
    'flex items-center text-slate-500 hover:text-slate-800',
};

const sizeClasses: Record<ButtonSize, string> = {
  [ButtonSize.extraLarge]: 'px-6 py-3',
  [ButtonSize.large]: 'p-2 text-sm h-7 sm:p-3 sm:text-base sm:h-10',
  [ButtonSize.medium]: 'p-2 text-xs',
  [ButtonSize.small]: 'p-1.5 text-xs',
  [ButtonSize.smallIcon]: 'p-1.5 text-xs',
  [ButtonSize.fullWidth]: 'py-3 text-base h-10 w-full justify-center',
  [ButtonSize.default]: 'm-0 text-xs',
};

const StyledIcon = styled(Icon)<{ $isRight: boolean; $withMargin: boolean }>`
  margin: ${({ $isRight, $withMargin }) =>
    $withMargin ? ($isRight ? '0 0 0 0.5rem' : '0 0.5rem 0 0') : ''};
  order: ${({ $isRight }) => ($isRight ? '1' : '')};
  z-index: 1;
`;

interface Props extends AriaAttributes {
  id?: string;
  color?: ButtonColor;
  size?: ButtonSize;
  className?: string;
  tabIndex?: number;
  disabled?: boolean;
  displayName?: string;
  isLoading?: boolean;
  icon?: Icons;
  iconSize?: IconSize;
  iconRight?: boolean;
  rotateIcon?: boolean;
  children?: ReactNode;
  rounded?: boolean;
  collapseInMobile?: boolean;
  type?: 'submit' | 'reset' | 'button' | undefined;
  name?: string;
  toolTip?: string;
  onClick?: () => void;
}

const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      color = ButtonColor.default,
      size = ButtonSize.default,
      tabIndex = 1,
      disabled = false,
      type = 'submit',
      icon,
      rounded = true,
      iconRight = false,
      iconSize,
      rotateIcon,
      name,
      className,
      id,
      children,
      onClick,
      isLoading,
      toolTip,
      collapseInMobile = false,
      "aria-expanded": ariaExpanded
    },
    ref
  ) => {
    const getIconColor = () => {
      if(disabled) return IconColor.gray;
      if (color === ButtonColor.primary) return IconColor.white;
      if (color === ButtonColor.secondary) return IconColor.white;
      if (color === ButtonColor.light) return "black";
      return IconColor.default;
    };

    const getIconSize = () => {
      if (size === ButtonSize.small) return IconSize.small;
      if (size === ButtonSize.medium) return IconSize.small;
      if (size === ButtonSize.large) return IconSize.medium;
      return IconSize.large;
    };

    const isMobile = useIsMobile();
    const collapse = isMobile && collapseInMobile || children == undefined;
    
    return (
      <TooltipInfo content={toolTip}>
      <button
        id={id}
        className={`${className} ${
          color !== ButtonColor.default && buttonClasses
        } ${!disabled ? colorClasses[color] : 'border-gray-600 bg-gray-200'} ${sizeClasses[size]} ${rounded && 'after:rounded-full rounded-full'}
        `
        }
        tabIndex={tabIndex}
        name={name}
        type={type}
        onClick={!disabled ? onClick : () => {}}
        disabled={disabled}
        aria-expanded={ariaExpanded}
        ref={ref}>
        {icon && (
          <StyledIcon
            icon={Icons[icon]}
            color={getIconColor()}
            size={iconSize || getIconSize()}
            rotate={rotateIcon}
            $isRight={iconRight}
            $withMargin={!collapse}
          />
        )}
        {collapse ? null : (
          <Fragment>
          {children}
          </Fragment>
        )}
        {isLoading && (
          <StyledIcon
            icon={Icons.loading}
            color={getIconColor()}
            size={iconSize || getIconSize()}
            rotate={true}
            $isRight={true}
            $withMargin={!collapse}
          />
        )}
      </button>
      </TooltipInfo>
    );
  }
);

Button.displayName = 'Buttons';

export default Button;
